import React from 'react';
import { ContentType } from 'types/publishedAssets';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { NotificationSignupCard } from './NotificationSignupCard';
import { AnonymousSignupForm } from './AnonymousSignupForm';
import { AuthenticatedOptIn } from './AuthenticatedOptIn';
import { AlreadySubscribed } from './AlreadySubscribed';
import { useNotificationSignupState } from './useNotificationSignupState';
import { resolveSignupBody } from './signupBodyKeys';

interface NotificationSignupProps {
  content: ContentType;
}

export function NotificationSignup({ content }: NotificationSignupProps) {
  const { translate } = useGetContentTranslations();
  const title = translate(content.translations, 'title');
  const bodyText = resolveSignupBody((key) => translate(content.translations, key));
  const buttonLabel = translate(content.translations, 'button_label');

  const showIllustration = content.metadata.show_illustration !== false;
  const hasBackgroundContainer = content.metadata.background_container !== false;
  const configuredIds = content.metadata.resident_custom_publication_ids ?? [];
  // Absent on pages published before the consent snapshot shipped.
  const consents = content.metadata.resident_custom_publication_consents ?? [];

  const state = useNotificationSignupState(configuredIds);

  return (
    <NotificationSignupCard
      title={title}
      bodyText={bodyText}
      showIllustration={showIllustration}
      hasBackgroundContainer={hasBackgroundContainer}
    >
      {state.kind === 'anonymous' && (
        <AnonymousSignupForm
          buttonLabel={buttonLabel}
          residentCustomPublicationIds={configuredIds}
          consents={consents}
        />
      )}
      {state.kind === 'optIn' &&
        (state.isAlreadySubscribed ? (
          <AlreadySubscribed consents={consents} />
        ) : (
          <AuthenticatedOptIn
            email={state.email}
            missingPublicationIds={state.missingPublicationIds}
            showSubmitButton
            buttonLabel={buttonLabel}
            consents={consents}
          />
        ))}
    </NotificationSignupCard>
  );
}
